import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Card, Image, Icon, Loader, Checkbox } from "semantic-ui-react";
import BCButton from "../ui/BCButton";
import BCCards from "../ui/BCCards";
import BCQuoteIcon from "../../assets/bc-quote-icon.png";
import BCTable from "../ui/BCTable";
import { guid, isValidPermission } from "../../Helpers";
import "../../styles/landing-page.scss";
import "../../styles/main.scss";
import BCKanban from "../ui/BCKanban";
import { BCFilterBar } from "../BCFilter";
import BCFilterSearch from "../BCFilterSearch";
import BCMenuBar from "../ui/BCMenuBar";
import { useBCGlobalFilter, useFetchData } from "../../helpers/GlobalHelper";
import { GlobalConstants } from "../constants/GlobalConstants";
import LmsMessage from "../lms/ui/LmsMessage";
import { getIsWhiteLabeled } from "../../helpers/PermissionHelper";

const getGmrViews = (gmrInfoList = [] as any[]) => {
  const gmrViews = [] as any[];
  const uniqueInfolist = gmrInfoList?.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i);
  uniqueInfolist?.forEach((gmrInfoListItem) => {
    const gmrView = {
      id: gmrInfoListItem.id,
      name: gmrInfoListItem.name,
      loanProgram: gmrInfoListItem.loanProgram,
      numberOfPropertiesOrUnits: gmrInfoListItem.numberOfUnitsProperties,
      submittedOn: gmrInfoListItem.submittedOn,
      brokerage: gmrInfoListItem.brokerage,
      brokerEmail: gmrInfoListItem.brokerEmail,
      statusLookupName: gmrInfoListItem.statusLookupName,
      amount: gmrInfoListItem.loanCommitment,
      closingDate: gmrInfoListItem.closingDate,
      assignedTo: gmrInfoListItem.assignedTo,
      channels: gmrInfoListItem.channels
    };

    gmrViews.push(gmrView);
  });

  return gmrViews;
};

const BPPricingKanbanLanes =
  {
    New: {
      name: "Pricing in the Works",
      index: 0
    },
    Sent: {
      name: "Pricing Sent",
      index: 1
    },
    Accepted: {
      name: "Pricing Accepted",
      index: 2
    }
  };

const sortGmrsKanban = (pricingData) => {
  const kanbanView = [
    {
      index: BPPricingKanbanLanes.New.index,
      name: BPPricingKanbanLanes.New.name,
      title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.New.index],
      cards: [] as any
    },
    {
      index: BPPricingKanbanLanes.Sent.index,
      name: BPPricingKanbanLanes.Sent.name,
      title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.Sent.index],
      cards: [] as any
    },
    {
      index: BPPricingKanbanLanes.Accepted.index,
      name: BPPricingKanbanLanes.Accepted.name,
      title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.Accepted.index],
      cards: [] as any
    }
  ];

  pricingData.forEach((application) => {
    if (application.statusLookupName && application.name !== null) {
      const kanbanObjIndex = kanbanView.findIndex(x => x.title === application.statusLookupName);
      if (kanbanObjIndex !== -1) {
        kanbanView[kanbanObjIndex].cards.push(application);
      }
    }
  });

  return kanbanView;
};

const BPPricing2 = ({
  isGreaterThanMobile,
  isGreaterThanTablet,
  isSidebar,
  proxyUser,
  permissions,
  brokerColor,
  setCopiedGmrId,
  setIsPricingStartScenarios
 }: any) => {
  const [isKanbanView, setIsKanbanView] = useState(localStorage.getItem("infolist") === "table" ? false : true);
  const [isFetching, setIsFetching] = useState(false);
  const [kanbanViews, setKanbanViews] = useState([] as any[]);
  const [currentSliceTo, setCurrentSliceTo] = useState(GlobalConstants.DefaultValues.defaultSliceKanban);
  const [{ data: gmrInfoList, error, isLoading: GMRInfoIsLoading, errorIsVisible, setErrorIsVisible, fetchData }, getGMRInfoAysnc] = useFetchData("gmr/infolist", { proxyUserId: proxyUser?.id });
  const GMRInfoData = useMemo(() => getGmrViews(gmrInfoList), [gmrInfoList]);
  const reducerInstance = useBCGlobalFilter(GMRInfoData, { hiddenFilters: ["Closing Date"], filterKeysList: ["loanProgram", "brokerage", "brokerEmail", "name"] });
  const [{ isFilterOpen, getFilteredData, filteredData: filteredGmrData, filterBySearchValue }]: any = reducerInstance;
  const history = useHistory();
  const handleSummary = useCallback((id) => {
    setIsPricingStartScenarios(true);
    history.push(`/getmyrate/${id}`);
  }, [history]);

  const handleGMR = useCallback((gmrObj) => {
    setCopiedGmrId(undefined);
    history.push(`/getmyrate/${gmrObj.id ? gmrObj.id : guid()}`);
  }, [history, setCopiedGmrId]);

  const handleCopyGMR = useCallback((gmrObj) => {
    setCopiedGmrId(gmrObj.id);
    history.push(`/getmyrate/${guid()}`);
  }, [history, setCopiedGmrId]);

  const handleDropGmrCard = async (card, i) => {
    const gmrObj = {
      gmrId: card.id,
      status: Object.getOwnPropertyNames(BPPricingKanbanLanes)[i]
    };

    await fetchData("gmr/update-status", { data: gmrObj, fetchMethod: "PUT" });
    getGMRInfoAysnc(false);
  };

  const getFilteredOrEmptyData = (kanbanData) => {
    if (!kanbanData) {
      return [];
    }

    return getFilteredData(kanbanData.cards);
  };

  const sortFilteredGmrsKanban = () => {
    const kanbanView = [
      {
        index: BPPricingKanbanLanes.New.index,
        name: BPPricingKanbanLanes.New.name,
        title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.New.index],
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPPricingKanbanLanes.New.index])).slice(0, currentSliceTo)
      },
      {
        index: BPPricingKanbanLanes.Sent.index,
        name: BPPricingKanbanLanes.Sent.name,
        title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.Sent.index],
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPPricingKanbanLanes.Sent.index])).slice(0, currentSliceTo)
      },
      {
        index: BPPricingKanbanLanes.Accepted.index,
        name: BPPricingKanbanLanes.Accepted.name,
        title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.Accepted.index],
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPPricingKanbanLanes.Accepted.index])).slice(0, currentSliceTo)
      }
    ];

    return kanbanView;
  };

  const sortedGmrsKanban = useMemo(() => sortFilteredGmrsKanban(), [filteredGmrData, currentSliceTo]);
  const convertToTableFormat = (gmrs) => {
    const result = [] as any[];
    gmrs.forEach((gmr) => {
      if (gmr.name !== null) {
        result.push({
          "id": gmr.id,
          "Name": gmr.name,
          "Loan Program": gmr.loanProgram,
          "Properties or Units": gmr.numberOfPropertiesOrUnits,
          "Submitted On": gmr.submittedOn ? new Date(gmr.submittedOn).toLocaleDateString() : null
        });
      }
    });

    return result;
  };

  const handleInfolistViewType = () => {
    const infolistStyle = isKanbanView ? "table" : "card";
    localStorage.setItem("infolist", infolistStyle);
    setIsKanbanView(!isKanbanView);
  };

  const handleScroll = () => {
    if (window.innerHeight - document.documentElement.scrollTop !== document.documentElement.clientHeight) {
      setIsFetching(true);
    }
    else {
      return;
    }
  };

  useEffect(() => {
    if (GMRInfoData) {
      setKanbanViews(sortGmrsKanban(GMRInfoData));
    }
  }, [GMRInfoData]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isFetching) {
      return;
    }

    setCurrentSliceTo(currentSliceTo + 5);
    setIsFetching(false);
  }, [isFetching]);

  useEffect(() => {
    if (currentSliceTo !== GlobalConstants.DefaultValues.defaultSliceKanban) {
      setCurrentSliceTo(GlobalConstants.DefaultValues.defaultSliceKanban);
    }
  }, [filteredGmrData]);

  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
      {GMRInfoIsLoading ? <Loader className="" active inline="centered" size={"big"} /> : <div>
        <Card.Group>
          <LmsMessage
            visible={errorIsVisible}
            error={error}
            onDismiss={() => setErrorIsVisible(false)}
          />
          <BCMenuBar
            leftChildren={[<React.Fragment>
              {isGreaterThanMobile ?
              <div className="landing-page-button-group">
                {!getIsWhiteLabeled() && <BCButton labelPosition="left" iconName="add" brokerColor={brokerColor} handleOnClick={() => handleGMR({})}>
                  <div>Start Pricing</div>
                </BCButton>}
                {(GMRInfoData && filteredGmrData?.length !== 0) && <Checkbox 
                  className="view-toggle" 
                  toggle 
                  label={{children: isKanbanView ? "Switch to table view!" : "Switch to card view!" }} 
                  onChange={() => handleInfolistViewType()} 
                  defaultChecked={!isKanbanView}
                />}
              </div> : <BCButton brokerColor={brokerColor} handleOnClick={handleGMR}>
                <Icon className="add" fitted />
              </BCButton>
            }</React.Fragment>]}
            rightChildren={[<BCFilterSearch reducerInstance={reducerInstance} hideFilterBar={!isValidPermission(permissions, "GlobalAdminAccess", "read")} />]}
            className={isValidPermission(permissions, "GlobalAdminAccess", "read") && isFilterOpen ? "no-bottom-margin" : ""}
          />
          {isValidPermission(permissions, "GlobalAdminAccess", "read") && <BCFilterBar reducerInstance={reducerInstance}/>}
        </Card.Group>
        {GMRInfoData && filteredGmrData?.length === 0 ?
          <Card.Group>
            <Card fluid>
              <div className="landing-page-empty-container">
                <div className="landing-page-empty-content">
                  {!getIsWhiteLabeled() && <Image className="landing-page-empty-icon center" src={BCQuoteIcon} />}
                  <div className="landing-page-empty-text">Looks like there isn't any pricing requests! Click below and get your loan priced in five minutes or less.</div>
                  <div className="center">
                    <BCButton brokerColor={brokerColor} handleOnClick={() => handleGMR({})}><div>Get Started</div></BCButton>
                  </div>
                </div>
              </div>
            </Card>
          </Card.Group> : isKanbanView === true && isGreaterThanMobile ? 
          <div className="broker-kanban-container">
            <BCKanban
              data={kanbanViews && kanbanViews[0] ? sortedGmrsKanban : []}
              setData={setKanbanViews}
              isGreaterThanMobile={isGreaterThanMobile} 
              infoData={filteredGmrData}
              handleCopyGMR={handleCopyGMR}
              displayList={filteredGmrData} 
              handleGMR={handleGMR}
              handleSummary={(id) => handleSummary(id)}
              ellipsis
              labels={[
                {
                  type: "progress",
                  condition: "submittedOn",
                  text: "In Progress",
                  color: "yellow"
                },
                {
                  type: "completed",
                  completed: true,
                  condition: "submittedOn",
                  text: "Completed",
                  color: "green"
                }
              ]}
              brokerColor={brokerColor}
              handleCustomDropCard={(card, i) => handleDropGmrCard(card, i)}
            />
          </div> : isKanbanView === true && isGreaterThanTablet === false ? <React.Fragment>
            <BCCards 
              isGreaterThanMobile={isGreaterThanMobile} 
              infoData={GMRInfoData} 
              handleInfo={handleGMR} 
              displayList={filteredGmrData}
              handleSummary={handleSummary}
              handleGMR={handleGMR}
              ellipsis
              permissions={permissions}
              isKanban={false}
              brokerColor={brokerColor}
              handleCopyGMR={handleCopyGMR}
            />
          </React.Fragment> : isKanbanView === false ? <React.Fragment>
            <BCTable 
              data={convertToTableFormat(filteredGmrData)}
              dataRules={[{ field: "id", rule: "hide" }]}
              handleClick={(index) => GMRInfoData[index] ? handleSummary(GMRInfoData[index].id) : void 0}
              selectable
              handleGMR={handleGMR}
              ellipsis
              brokerColor={brokerColor}
              sortable={["Name", "Loan Program", "Submitted On", "Properties or Units"]}
              pagination={25}
              handleCopyGMR={handleCopyGMR}
            />
          </React.Fragment> : null
        }
      </div>}
    </div>
  );

};

export default BPPricing2;